/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable
} from '@angular/material/table';
import { MatTabLink, MatTabNav, MatTabNavPanel } from '@angular/material/tabs';
import { MatToolbar } from '@angular/material/toolbar';
import { Title } from '@angular/platform-browser';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ShowHideDirective } from '@ngbracket/ngx-layout';
import { LayoutAlignDirective, LayoutDirective, LayoutGapDirective } from '@ngbracket/ngx-layout/flex';
import { RouterError } from '@ngxs/router-plugin';
import { Actions, ofAction, ofActionSuccessful, Store } from '@ngxs/store';
import { ReportDisplayService } from '@reports/services/report-display.service';
import { combineLatest, Observable, Subject } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { IconLoaderService } from '@shared/services/iconLoader.service';
import { LoggerService } from '@shared/services/logger.service';
import { UserToolsComponent } from '../security/components/user-tools/user-tools.component';
import { AuthState } from '../security/state/auth.state';
import { AppPageRoute, MenuBarImageConfig } from '../state/appConfig/app-config.model';
import { AppConfigState } from '../state/appConfig/app-config.state';
import { ShowInfo, ShowSuccess, ThrowError } from '../state/notification/notification.actions';
import { SnackMessage } from '../state/notification/notification.model';
import { PageState } from '../state/page/page.state';
import { AlertComponent } from '../ui/alert/alert.component';
import { HtmlSnackbarComponent } from '../ui/html-snackbar/html-snackbar.component';
import { ThemeSwitcherComponent } from '../ui/theme-switcher/theme-switcher.component';

@Component({
    selector: 'dash-gis-root',
    templateUrl: './root.component.html',
    styleUrls: ['./root.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatToolbar,
        LayoutDirective,
        LayoutAlignDirective,
        ShowHideDirective,
        MatTabNav,
        MatTabLink,
        RouterLink,
        RouterLinkActive,
        LayoutGapDirective,
        ThemeSwitcherComponent,
        UserToolsComponent,
        MatTabNavPanel,
        RouterOutlet,
        MatTable,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        AsyncPipe,
    ],
})
export class RootComponent implements AfterViewInit, OnInit, OnDestroy {
    shouldShowNav$: Observable<boolean>;
    dummyData = [{ a: 1 }, { a: 4 }];

    validated$: Observable<boolean> = inject(Store).select(AuthState.validated);
    loaded$: Observable<boolean> = inject(Store).select(AppConfigState.loaded);
    siteName$: Observable<string> = inject(Store).select(AppConfigState.siteName);
    menuBarImage$: Observable<MenuBarImageConfig> = inject(Store).select(AppConfigState.menuBarImage);
    enabledPages$: Observable<AppPageRoute[]> = inject(Store).select(PageState.enabledPages);
    private _pageEnabled$: Observable<(arg0: string) => boolean> = inject(Store).select(PageState.pageEnabled);

    private disposeSubject = new Subject<void>();

    private snackQueue: SnackMessage[] = [];
    private snackTrigger$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private title: Title,
        private iconLoader: IconLoaderService,
        private snackbar: MatSnackBar,
        private dialog: MatDialog,
        private rds: ReportDisplayService,
        private actions$: Actions
    ) {
        this.iconLoader.loadIcons();
        this.shouldShowNav$ = combineLatest([this.enabledPages$, this.validated$]).pipe(
            map(([pages, validated]) => validated && pages.length > 1)
        );

        this.snackTrigger$
            .pipe(
                filter(() => this.snackQueue.length > 0),
                map(() => this.snackQueue.shift()),
                concatMap((snackData) => {
                    let config;
                    switch (snackData.type) {
                        case 'error':
                            config = {
                                data: {
                                    message: snackData.message,
                                    action: {
                                        label: 'Error',
                                        color: 'error',
                                        useCloseButton: true,
                                    },
                                },
                            };
                            break;
                        case 'warning':
                            config = {
                                data: {
                                    message: snackData.message,
                                    action: {
                                        label: 'Warning',
                                        color: 'warn',
                                        useCloseButton: true,
                                    },
                                },
                            };
                            break;
                        case 'success':
                            config = {
                                duration: 5000,
                                data: {
                                    message: snackData.message,
                                    action: {
                                        label: 'Success',
                                        color: 'success',
                                        useCloseButton: false,
                                    },
                                },
                            };
                            break;
                        case 'info':
                            config = {
                                duration: 5000,
                                data: {
                                    message: snackData.message,
                                    action: {
                                        label: 'Info',
                                        color: 'default',
                                        useCloseButton: false,
                                    },
                                },
                            };
                    }
                    return this.snackbar.openFromComponent(HtmlSnackbarComponent, config).afterDismissed();
                }),
                tap(() => this.snackTrigger$.next())
            )
            .subscribe();
    }

    ngOnInit() {
        this.loaded$
            .pipe(
                filter((b) => b),
                switchMap(() => this.siteName$)
            )
            .subscribe((siteName) => this.title.setTitle(siteName));

        this.actions$.pipe(ofActionSuccessful(ThrowError)).subscribe((action: ThrowError) => {
            this.logger.error(action.exception);
            if (action.exception?.message) {
                this.dialog.open(AlertComponent, {
                    width: '350px',
                    minHeight: '300px',
                    data: action.exception.message,
                });
            }
        });

        // this.actions$.pipe(ofActionSuccessful(ShowError)).subscribe((action: ShowError) => {
        //     this.addSnack({ message: action.message, type: 'error' });
        // });
        //
        // this.actions$.pipe(ofActionSuccessful(ShowWarning)).subscribe((action: ShowWarning) => {
        //     this.addSnack({ message: action.message, type: 'warning' });
        // });

        this.actions$.pipe(ofActionSuccessful(ShowSuccess)).subscribe((action: ShowSuccess) => {
            this.addSnack({ message: action.message, type: 'success' });
        });

        this.actions$.pipe(ofActionSuccessful(ShowInfo)).subscribe((action: ShowInfo) => {
            this.addSnack({ message: action.message, type: 'info' });
        });

        this.actions$.pipe(ofAction(RouterError)).subscribe((action: RouterError<any, any>) => {
            this.logger.error('RouterError:', action);
        });

        this.validated$
            .pipe(
                distinctUntilChanged(),
                filter((b) => b)
            )
            .subscribe(() => this.rds.init());
    }

    ngAfterViewInit() {
        // when drag/drop is enable (for file uploads), these prevent
        // dropping a file outside the target area and having it load over the page
        window.addEventListener(
            'dragover',
            (e) => {
                e.preventDefault();
                e.dataTransfer.effectAllowed = 'none';
                e.dataTransfer.dropEffect = 'none';
            },
            false
        );

        window.addEventListener(
            'drop',
            (e) => {
                e.preventDefault();
                e.dataTransfer.effectAllowed = 'none';
                e.dataTransfer.dropEffect = 'none';
            },
            false
        );
    }

    pageEnabled$(page: string) {
        return this._pageEnabled$.pipe(map((fn) => fn(page)));
    }

    ngOnDestroy() {
        this.disposeSubject.next();
    }

    addSnack(message: SnackMessage) {
        const last = this.snackQueue.length;
        this.snackQueue.push(message);
        if (last === 0) {
            this.snackTrigger$.next();
        }
    }
}
